/** @jsxImportSource @emotion/react */
'use memo'

import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import { PlayerHighlight, HighlightTeam } from '../../../Player/types'
import Icon from '../../../../components/Icon'
import {
  cardStyle,
  cardThumbnailStyle,
  cardThumbnailActiveStyle,
  playOverlayStyle,
  playOverlayVisibleStyle,
  cardTextContainerStyle,
  teamsRowStyle,
  teamsRowLinkStyle,
  teamAbbrStyle,
  teamLogoStyle,
  vsLabelStyle,
  cardTitleStyle,
  cardTitleActiveStyle,
  cardDateStyle,
} from './styles'

const PLACEHOLDER_THUMBNAIL =
  '/images/generic_placeholders/placeholder_team.png'

const formatDate = (iso?: string): string => {
  if (!iso) {
    return ''
  }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    return ''
  }
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const getTeamLogo = (team?: HighlightTeam): string | undefined => {
  const image: unknown = team?.image
  if (!image) {
    return undefined
  }
  if (typeof image === 'string') {
    return image
  }
  if (typeof image === 'object' && image !== null && 'full_path' in image) {
    const url = resolveAssetUrl(image as { full_path?: string })
    return url ?? undefined
  }
  return undefined
}

interface Props {
  highlight: PlayerHighlight
  isActive: boolean
  itemCount: number
  onClick: (highlight: PlayerHighlight) => void
}

const HighlightCardItem: React.FC<Props> = ({
  highlight,
  isActive,
  itemCount,
  onClick,
}) => {
  const home = highlight.game?.home_team
  const visitor = highlight.game?.visiting_team
  const homeLogo = getTeamLogo(home)
  const visitorLogo = getTeamLogo(visitor)
  const gameId = highlight.game?.id
  const gameUrl = gameId ? ROUTES.GAME_PAGES.replace(/:id/, gameId) : null

  return (
    <div
      css={cardStyle(itemCount)}
      onClick={() => onClick(highlight)}
      role='button'
      tabIndex={0}
      aria-label={`Play highlight: ${highlight.title}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick(highlight)
        }
      }}
    >
      <div
        css={[cardThumbnailStyle, isActive && cardThumbnailActiveStyle]}
        data-card-thumbnail
      >
        <img
          src={
            resolveAssetUrl(highlight.thumbnail_url) || PLACEHOLDER_THUMBNAIL
          }
          alt={highlight.title}
        />
        <div
          css={[playOverlayStyle, isActive && playOverlayVisibleStyle]}
          data-play-overlay
        >
          <Icon
            name='play-circle'
            faType='far'
            fontSize={32}
            color={colors.WHITE}
          />
        </div>
      </div>

      <div css={cardTextContainerStyle}>
        {(home || visitor) &&
          (gameUrl ? (
            <a
              href={gameUrl}
              target='_blank'
              rel='noopener noreferrer'
              css={[teamsRowStyle, teamsRowLinkStyle]}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Open game ${home?.abbreviation ?? ''} vs ${
                visitor?.abbreviation ?? ''
              } in a new tab`}
            >
              {home && <span css={teamAbbrStyle}>{home.abbreviation}</span>}
              {homeLogo && (
                <img css={teamLogoStyle} src={homeLogo} alt={home?.name} />
              )}
              <span css={vsLabelStyle}>vs</span>
              {visitorLogo && (
                <img
                  css={teamLogoStyle}
                  src={visitorLogo}
                  alt={visitor?.name}
                />
              )}
              {visitor && (
                <span css={teamAbbrStyle}>{visitor.abbreviation}</span>
              )}
            </a>
          ) : (
            <div css={teamsRowStyle}>
              {home && <span css={teamAbbrStyle}>{home.abbreviation}</span>}
              {homeLogo && (
                <img css={teamLogoStyle} src={homeLogo} alt={home?.name} />
              )}
              <span css={vsLabelStyle}>vs</span>
              {visitorLogo && (
                <img
                  css={teamLogoStyle}
                  src={visitorLogo}
                  alt={visitor?.name}
                />
              )}
              {visitor && (
                <span css={teamAbbrStyle}>{visitor.abbreviation}</span>
              )}
            </div>
          ))}

        <div
          css={[cardTitleStyle, isActive && cardTitleActiveStyle]}
          data-card-title
        >
          {highlight.title}
        </div>
        <div css={cardDateStyle}>{formatDate(highlight.created_at)}</div>
      </div>
    </div>
  )
}

export default HighlightCardItem
